import type { ReactElement } from 'react';
import { CaptureContentsArt, CaptureRouteArt, CaptureWaysArt } from '../projects/CaptureIllustrations';
import { Page, WidgetFlowArt } from '../widget/WidgetIllustrations';
import shared from '../widget/WidgetIllustrations.module.css';
import type { TourArtName } from './tours';
import styles from './TourArt.module.css';

/**
 * The drawing above a tour step's words (TOADO-988). The app's own drawings
 * where one says the thing already (what a capture brings, how a page finds
 * its project, the widget, the extension), so the tour and the settings it
 * points at look alike; two small ones here for what has none. Decoration
 * only: the step's words say the same.
 */
export function TourArt({ name }: { name: TourArtName }) {
  const drawings: Record<TourArtName, () => ReactElement> = {
    capture: () => <CaptureContentsArt />,
    session: () => <SessionArt />,
    route: () => <CaptureRouteArt />,
    widget: () => <WidgetFlowArt step={2} />,
    agent: () => <AgentArt />,
    extension: () => <CaptureWaysArt />,
    sources: () => <SourcesArt />,
  };
  return <div className={styles.art} data-art={name} data-testid={`tour-art-${name}`}>{drawings[name]()}</div>;
}

/**
 * Where tickets come from (Jason, 28 Oct): the extension in a browser's
 * toolbar and the bug collector's button on a site, each feeding the board.
 */
function SourcesArt() {
  return (
    <svg className={shared.art} viewBox="0 0 200 116" aria-hidden="true" focusable="false">
      <g transform="translate(4 2)">
        <Page width={88} height={56} header={false} />
        {/* The extension's icon, in the toolbar. */}
        <rect className={shared.accent} x={72} y={3.5} width={9} height={7} rx={2} />
        <rect className={shared.ink} x={9} y={20} width={36} height={4} rx={2} />
        <rect className={shared.faint} x={9} y={28} width={60} height={3} rx={1.5} />
        <rect className={shared.block} x={9} y={35} width={44} height={14} rx={2.5} />
        <rect className={shared.outline} x={7} y={33} width={48} height={18} rx={3.5} />
      </g>
      <text className={styles.label} x={48} y={70} textAnchor="middle">Extension</text>
      <g transform="translate(108 2)">
        <Page width={88} height={56} header={false} />
        <rect className={shared.ink} x={9} y={20} width={40} height={4} rx={2} />
        <rect className={shared.faint} x={9} y={28} width={56} height={3} rx={1.5} />
        <rect className={shared.faint} x={9} y={34} width={48} height={3} rx={1.5} />
        {/* The bug collector's launcher, bottom right. */}
        <circle className={shared.accent} cx={76} cy={44} r={6.5} />
        <circle className={shared.onAccent} cx={76} cy={44} r={2} />
      </g>
      <text className={styles.label} x={152} y={70} textAnchor="middle">Bug collector</text>
      <path className={styles.link} d="M48 75 C48 88 60 94 72 96" />
      <path className={styles.link} d="M152 75 C152 88 140 94 128 96" />
      {/* The board: three columns, a card landing in the first. */}
      {[74, 94, 114].map((x, column) => (
        <g key={x}>
          <rect className={styles.column} x={x} y={84} width={18} height={30} rx={3} />
          <rect className={column === 0 ? shared.accent : shared.block} x={x + 3} y={88} width={12} height={7} rx={1.5} />
          {column < 2 && <rect className={shared.block} x={x + 3} y={98} width={12} height={7} rx={1.5} />}
        </g>
      ))}
    </svg>
  );
}

/** The visit, oldest first: a page, two clicks, a failed request and an error. */
function SessionArt() {
  const rows = [
    { y: 18, width: 58, mark: shared.ink },
    { y: 32, width: 44, mark: shared.ink },
    { y: 46, width: 52, mark: shared.ink },
    { y: 60, width: 48, mark: styles.bad },
    { y: 74, width: 62, mark: styles.bad },
  ];
  return (
    <svg className={shared.art} viewBox="0 0 160 90" aria-hidden="true" focusable="false">
      <Page width={160} height={90} header={false} />
      <line className={shared.rule} x1={22} y1={14} x2={22} y2={80} />
      {rows.map((row) => (
        <g key={row.y}>
          <circle className={row.mark} cx={22} cy={row.y} r={3} />
          <rect className={shared.faint} x={32} y={row.y - 2} width={row.width} height={4} rx={2} />
          <rect className={shared.block} x={128} y={row.y - 3} width={20} height={6} rx={2} />
        </g>
      ))}
    </svg>
  );
}

/** An agent's terminal reading a ticket from the board. */
function AgentArt() {
  return (
    <svg className={shared.art} viewBox="0 0 160 90" aria-hidden="true" focusable="false">
      <rect className={styles.terminal} x={6} y={12} width={82} height={66} rx={5} />
      <rect className={styles.prompt} x={14} y={24} width={6} height={4} rx={1} />
      <rect className={styles.line} x={24} y={24} width={44} height={4} rx={2} />
      <rect className={styles.line} x={14} y={36} width={60} height={4} rx={2} />
      <rect className={styles.line} x={14} y={46} width={50} height={4} rx={2} />
      <rect className={styles.prompt} x={14} y={58} width={6} height={4} rx={1} />
      <path className={styles.link} d="M92 45 H108" />
      <rect className={shared.page} x={110} y={22} width={44} height={46} rx={4} />
      <rect className={shared.accent} x={116} y={30} width={14} height={4} rx={2} />
      <rect className={shared.ink} x={116} y={40} width={32} height={4} rx={2} />
      <rect className={shared.faint} x={116} y={49} width={26} height={3} rx={1.5} />
      <rect className={shared.faint} x={116} y={56} width={20} height={3} rx={1.5} />
    </svg>
  );
}
